import React from 'react';
import PropTypes from 'prop-types';

import {
    LocaleString,
    FormattedNumber,
    FormattedDate,
    FormattedTime,
    getString,
    keyExists,
} from '@nackle/intl-tools';
import { Tooltip } from 'antd';
import { WarningOutlined } from '@ant-design/icons';


/**
 * Renders a component containing the provided string in the language
 * specified by the locale code stored in `intl-tools`. This component
 * is simply a wrapper of a component from `intl-tools`. Note that for
 * this string to be properly translated, a language bundle must be
 * loaded into `intl-tools` with the appropriate keys.
 */
export const Translate = ( props ) => {
    if ( !keyExists( props.value ) ) {
        console.info( `Could not find translation for ${ props.value } in current locale` );
        return (
            <Tooltip title={ 'No translation available' }>
                <WarningOutlined />
            </Tooltip>
        );
    }
    return (
        <LocaleString
            className={ props.className }
            style={ props.style }
            code={ props.value }
            replaceOptions={ props.variables }
            TextComponent={ props.component }
            stripHTML={ props.stripHTML }
            escapeHTML={ !props.convertHTML }
        />
    );
};

export const getTranslatedString = ( key ) => {
    return getString( key );
};

Translate.defaultProps = {
    className: undefined,
    style: undefined,
    value: undefined,
    variables: undefined,
    component: undefined,
    stripHTML: false,
    convertHTML: true,
};

Translate.propTypes = {
    className: PropTypes.string,
    style: PropTypes.object,
    value: PropTypes.string,
    variables: PropTypes.object,
    component: PropTypes.node,
    stripHTML: PropTypes.bool,
    convertHTML: PropTypes.bool,
};


/**
 * Renders a component containing the provided string in the language
 * specified by the locale code stored in `intl-tools`. This component
 * is simply a wrapper of a component from `intl-tools`. Note that for
 * this string to be properly translated, a language bundle must be
 * loaded into `intl-tools` with the appropriate keys.
 */
Translate.Text = ( props ) => {
    return <Translate { ...props } />;
};

Translate.Text.defaultProps = {
    className: undefined,
    style: undefined,
    value: undefined,
    variables: undefined,
    component: undefined,
    stripHTML: false,
    convertHTML: true,
};

Translate.Text.propTypes = {
    className: PropTypes.string,
    style: PropTypes.object,
    value: PropTypes.string,
    variables: PropTypes.object,
    component: PropTypes.node,
    stripHTML: PropTypes.bool,
    convertHTML: PropTypes.bool,
};


/**
 * Renders a component containing the provided number in the language
 * specified by the locale code stored in `intl-tools`. This component
 * is simply a wrapper of a component from `intl-tools`.
 */
Translate.Number = ( props ) => {
    return (
        <FormattedNumber
            className={ props.className }
            style={ props.style }
            value={ props.value }
            TextComponent={ props.component }
            formatOptions={ props.formatOptions }
        />
    );
};

Translate.Number.defaultProps = {
    className: undefined,
    style: undefined,
    value: 0,
    component: undefined,
    formatOptions: undefined,
};

Translate.Number.propTypes = {
    className: PropTypes.string,
    style: PropTypes.object,
    value: PropTypes.number,
    component: PropTypes.node,
    formatOptions: PropTypes.object,
};


/**
 * Renders a component containing the provided date in the language
 * specified by the locale code stored in `intl-tools`. This component
 * is simply a wrapper of a component from `intl-tools`.
 */
Translate.Date = ( props ) => {
    return (
        <FormattedDate
            className={ props.className }
            style={ props.style }
            value={ props.value }
            TextComponent={ props.component }
        />
    );
};

Translate.Date.defaultProps = {
    className: undefined,
    style: undefined,
    value: undefined,
    component: undefined,
    formatOptions: undefined,
};

Translate.Date.propTypes = {
    className: PropTypes.string,
    style: PropTypes.object,
    value: PropTypes.oneOfType( [ PropTypes.string, PropTypes.number, PropTypes.object ] ),
    component: PropTypes.node,
};


/**
 * Renders a component containing the provided time in the language
 * specified by the locale code stored in `intl-tools`. This component
 * is simply a wrapper of a component from `intl-tools`.
 */
Translate.Time = ( props ) => {
    return (
        <FormattedTime
            className={ props.className }
            style={ props.style }
            value={ props.value }
            TextComponent={ props.component }
        />
    );
};

Translate.Time.defaultProps = {
    className: undefined,
    style: undefined,
    value: undefined,
    component: undefined,
};

Translate.Time.propTypes = {
    className: PropTypes.string,
    style: PropTypes.object,
    value: PropTypes.oneOfType( [ PropTypes.string, PropTypes.number, PropTypes.object ] ),
    component: PropTypes.node,
};
